interface ErrorBannerProps {
  message: string;
  retryable?: boolean;
  onRetry?: () => void;
}

export function ErrorBanner({ message, retryable, onRetry }: ErrorBannerProps) {
  return (
    <div className="rounded-md bg-warning/10 border border-warning/20 p-4">
      <div className="flex items-start gap-3">
        <svg
          className="h-5 w-5 text-warning flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
          />
        </svg>
        <div className="flex-1">
          <p className="text-sm text-text">{message}</p>
          {retryable && onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-fast"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AuthRequiredBanner() {
  return (
    <div className="rounded-md bg-secondary/10 border border-secondary/20 p-4">
      <div className="flex items-start gap-3">
        <svg
          className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
          />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-text">Sign-in required</p>
          <p className="text-sm text-muted mt-1">Please sign in to access this content.</p>
          <button className="btn-secondary mt-3 text-sm py-1.5">Sign In</button>
        </div>
      </div>
    </div>
  );
}

export function LoadingSpinner({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg className="h-6 w-6 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

/** Default empty box icon */
function DefaultEmptyIcon() {
  return (
    <svg
      className="mx-auto h-12 w-12 text-muted"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
      />
    </svg>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  /** Custom icon component (defaults to empty box) */
  icon?: React.ReactNode;
}

export function EmptyState({ title, description, action, icon }: EmptyStateProps) {
  return (
    <div className="text-center py-12">
      {icon ?? <DefaultEmptyIcon />}
      <h3 className="mt-4 text-lg font-medium text-text">{title}</h3>
      {description && <p className="mt-1 text-sm text-muted max-w-md mx-auto">{description}</p>}
      {action && (
        <button className="btn-primary mt-4" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}
